/**
 * Status einer Aufgabe gehört immer zu einer Kategorie. „Erledigt“ heißt immer: Kategorie `done` und
 * `completedAt` gesetzt (wie KaBa `isDone`) – nie „letzte Spalte“.
 */
export const STATUS_CATEGORIES = ['open', 'active', 'done'] as const;
export type StatusCategory = (typeof STATUS_CATEGORIES)[number];

export const DEFAULT_STATUSES: ReadonlyArray<{ name: string; category: StatusCategory }> = [
  { name: 'Offen', category: 'open' },
  { name: 'In Arbeit', category: 'active' },
  { name: 'Erledigt', category: 'done' },
];

/** `guest` (P15) nur über Gast-Freigaben – nie über „Mitglied hinzufügen“ */
export const PROJECT_ROLES = ['lead', 'member', 'guest'] as const;
export type ProjectRole = (typeof PROJECT_ROLES)[number];
export const PROJECT_MEMBER_ROLES = ['lead', 'member'] as const;

export const TASK_TITLE_MAX = 500;
/** Mehrzeilen-Einfügen: höchstens so viele Aufgaben auf einmal */
export const BULK_MAX = 200;

/**
 * Text aus der Zwischenablage in Aufgabentitel zerlegen (P2: Mehrzeilen-Einfügen). Leere Zeilen fallen weg,
 * Aufzählungszeichen und Checkbox-Markierungen am Zeilenanfang werden entfernt.
 */
export function splitTitles(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((l) =>
      l
        .replace(/^\s*(?:[-*•–]|\d+[.)]|\[[ xX]?\])\s+/, '')
        .replace(/^\s*\[[ xX]?\]\s*/, '')
        .trim(),
    )
    .filter(Boolean)
    .map((l) => l.slice(0, TASK_TITLE_MAX));
}

/** Priorität (Paket P8): 1 = dringend … 4 = niedrig; null = keine (der Normalfall, Regel R2) */
export const PRIORITIES = [1, 2, 3, 4] as const;
export type Priority = (typeof PRIORITIES)[number];

/** Palettenfarben (schnelle Auswahl, geprüfte Hell-/Dunkelvarianten). ADR 0134: daneben jede eigene Farbe als #RRGGBB */
export const LABEL_COLORS = ['red', 'orange', 'amber', 'green', 'teal', 'blue', 'violet', 'pink', 'gray'] as const;
export type LabelColor = (typeof LABEL_COLORS)[number];

/** Eigene Felder (P18): feste, kleine Auswahl an Arten – die Art steht beim Anlegen fest */
export const FIELD_KINDS = ['text', 'number', 'date', 'select', 'person', 'checkbox'] as const;
export type FieldKind = (typeof FIELD_KINDS)[number];
export const FIELDS_MAX = 30;
export const FIELD_OPTIONS_MAX = 50;
export const FIELD_TEXT_MAX = 2000;

export const CHECKLIST_MAX = 300;

/**
 * Werkzeuge, die ein Projekt über die Aufgaben hinaus zuschalten kann (Paket P9, Konzept „Werkzeuge per +“).
 * Aufgaben sind immer da. Weitere Werkzeuge (Dokumente, Dateien, Diskussionen) folgen in Stufe 2.
 */
/** ADR 0064: `intake` = Projekteingang (standardmäßig aus, nie für Gäste) */
/** ADR 0071: `approvals` = Freigaben-Register (Abnahmen und Entscheidungen, standardmäßig aus) */
export const PROJECT_TOOLS = ['overview', 'files', 'docs', 'talk', 'intake', 'approvals', 'events', 'people', 'budget', 'resources', 'goals', 'time'] as const;
/** P20: wer im Projekt Freigabelinks erzeugen darf; „off“ legt alle Links still */
export const SHARING_MODES = ['members', 'leads', 'off'] as const;
export type SharingMode = (typeof SHARING_MODES)[number];
export const SHARE_KINDS = ['page', 'file', 'task'] as const;
export type ShareKind = (typeof SHARE_KINDS)[number];
export type ProjectTool = (typeof PROJECT_TOOLS)[number];

/**
 * Werkzeug-Registrierung (W1, ADR 0070): EIN Ort, der sagt, wer ein Werkzeug sieht. Server (Snapshot, Vorschau beim
 * Zuschalten) und Web lesen dieselbe Regel – ein neues Werkzeug trägt sich hier ein, bevor es eine Oberfläche bekommt.
 * - `all`: alle im Projekt, Inhalte nach Bereich/Freigabe gefiltert (Übersicht),
 * - `material`: wer Material sieht (ADR 0063 „Material aus“ blendet das Werkzeug aus),
 * - `members`: Leitung und Mitglieder jeder Stufe – nie Gäste (Ziele & Risiken),
 * - `editors`: Leitung und Mitglieder mit „Bearbeiten“ – nie Gäste (Eingang).
 * `sensitive`: Werkzeug mit Personen-, Kosten- oder Anfragedaten – Zuschalten zeigt ausdrücklich, wer es NICHT sieht.
 */
export type ToolAudience = 'all' | 'material' | 'members' | 'editors';
export interface ToolMeta {
  audience: ToolAudience;
  sensitive: boolean;
}
export const TOOL_REGISTRY: Record<ProjectTool, ToolMeta> = {
  overview: { audience: 'all', sensitive: false },
  files: { audience: 'material', sensitive: false },
  docs: { audience: 'material', sensitive: false },
  talk: { audience: 'material', sensitive: false },
  intake: { audience: 'editors', sensitive: true },
  approvals: { audience: 'all', sensitive: false },
  events: { audience: 'all', sensitive: false },
  /** ADR 0080: Personendaten zusätzlich nur mit gesondertem Recht (Leitung + freigeschaltete Mitglieder) */
  people: { audience: 'editors', sensitive: true },
  /** ADR 0082: Beträge nur für Bearbeitende, je Position nach Bereich */
  budget: { audience: 'editors', sensitive: true },
  /** ADR 0083: Planung nur Bearbeitende; alle anderen sehen im Werkzeug nur ihre eigenen Einsätze */
  resources: { audience: 'all', sensitive: false },
  /** ADR 0084: Register für Leitung und Mitglieder (jede Stufe) – Gäste nie */
  goals: { audience: 'members', sensitive: false },
  /** ADR 0085: eigene Zeiten; alle Personen nur für die Leitung */
  time: { audience: 'members', sensitive: true },
};
/** Wer ein Werkzeug sieht – `stage` ist die Stufe (Gast oder Mitglied mit Profil), `material` false = „Material aus“ */
export interface ToolViewer {
  lead: boolean;
  guest: boolean;
  stage: 'view' | 'comment' | 'execute' | 'edit';
  material: boolean;
}
export function toolVisible(tool: ProjectTool, v: ToolViewer): boolean {
  if (v.lead) return true;
  const a = TOOL_REGISTRY[tool].audience;
  if (a === 'all') return true;
  if (a === 'material') return v.material;
  if (a === 'members') return !v.guest;
  return !v.guest && v.stage === 'edit';
}

/** Projektstatus auf der Übersicht: im Plan / gefährdet / kritisch */
export const PROJECT_HEALTH = ['on_track', 'at_risk', 'critical'] as const;
export type ProjectHealth = (typeof PROJECT_HEALTH)[number];
/** P24: Phase eines Projekts (Lobby-Gruppen, Portfolio). Ohne Angabe gilt „läuft“. */
export const PROJECT_PHASES = ['planned', 'active', 'done'] as const;
export type ProjectPhase = (typeof PROJECT_PHASES)[number];

/** WIP-Limit je Board-Spalte (Hinweis, keine Sperre) */
export const WIP_LIMIT_MAX = 999;

/* ---------- P25: Aufgaben-Schlüssel ---------- */

/** Kürzel eines Projekts: 2–6 Großbuchstaben/Ziffern, beginnt mit einem Buchstaben (KURS, SOM, MV26) */
export const PROJECT_KEY_PATTERN = /^[A-Z][A-Z0-9]{1,5}$/;

/** Kürzel aus dem Projektnamen: Anfangsbuchstaben ab drei Wörtern, sonst die ersten vier Zeichen */
export function deriveProjectKey(name: string): string {
  const plain = name
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();
  const words = plain.split(/[^A-Z0-9]+/).filter(Boolean);
  let key = words.length >= 3 ? words.map((w) => w[0]).join('').slice(0, 4) : words.join('').slice(0, 4);
  if (!/^[A-Z]/.test(key)) key = `P${key}`;
  key = key.slice(0, 6);
  return key.length >= 2 ? key : (key + 'RJ').slice(0, 3);
}

/** Freies Kürzel: bei Belegung Ziffer anhängen (SOM → SOM2 …) */
export function uniqueProjectKey(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base;
  for (let i = 2; i < 1000; i++) {
    const n = String(i);
    const k = base.slice(0, 6 - n.length) + n;
    if (!taken.has(k)) return k;
  }
  return base;
}

/** „KURS-42“ → { key, number } (Groß-/Kleinschreibung egal) */
export function parseTaskKey(s: string): { key: string; number: number } | null {
  const m = /^\s*([A-Za-z][A-Za-z0-9]{1,5})-(\d{1,7})\s*$/.exec(s);
  return m ? { key: m[1]!.toUpperCase(), number: Number(m[2]) } : null;
}
